import {
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar
} from '@apollo/client'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import type { DocumentNode } from 'graphql'

import Flex from 'shared/ui/Flex'
import Head from 'shared/components/Head'
import PageHeader from 'shared/components/PageHeader'
import useAuth from 'data/gradoo/hooks/useAuth'
import { START_PROJECT_EXPORT } from 'data/layoutcreator/mutations/export'
import { START_COVER_PDF_EXPORT } from 'data/layoutcreator/mutations/projects'
import { GET_PRODUCTION_ATTRS } from 'data/layoutcreator/queries/erp'
import { GET_PRODUCTION_EXPORTS } from 'data/layoutcreator/queries/production'
import {
  CHECK_FOR_COVER_FILE,
  GET_PROJECT_META,
  GET_PROJECT_TOKEN
} from 'data/layoutcreator/queries/projects'
import { isExportLinkItemDisabledVar } from 'data/store/settings-export'
import IslandLayout from 'shared/layout/Island'
import { OUTPUT_TYPES } from 'screens/AdminOldProject/tabs/ExportTab/components/ProjectExportModal'
import {
  ProductionExportStatuses,
  ProductionExportType,
  UploadTypes
} from 'shared/types/layoutcreator/graphql'
import { getRestUrl } from 'shared/helpers/url'
import useInterval from 'shared/hooks/useInterval'

import History from './components/History'
import { ListItem } from './components/ListItem'
import ProductionModal from './components/ProductionModal'
import { Section } from './components/Section'
import ExportPendingModal from './components/ExportPendingModal'
import StatusModal, { Status } from './components/StatusModal'
import YouMadeIt from './components/YouMadeIt'
import { useProductionAttributes } from './hooks/use-production-attributes'
import { CHECK_IF_EXPORT_IS_IN_PROGRESS } from 'data/layoutcreator/queries/export'

const MODAL_WIDTH = 965

const getCoverExportUrl = ({
  projectId,
  groupId,
  coverFileId,
  token
}: {
  projectId: string
  groupId: string
  coverFileId: string
  token: string
}) =>
  getRestUrl(
    `cover/export?projectId=${encodeURIComponent(
      projectId
    )}&groupId=${encodeURIComponent(
      groupId
    )}&coverFileId=${encodeURIComponent(
      coverFileId
    )}&token=${encodeURIComponent(token)}`
  )

const downloadCoverPdf = ({
  projectId,
  groupId,
  coverFileId,
  token
}: {
  projectId: string
  groupId: string
  coverFileId: string
  token: string
}) => {
  const link = document.createElement('a')
  link.href = getCoverExportUrl({
    projectId,
    groupId,
    coverFileId,
    token
  })
  link.download = 'cover.pdf'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const ProjectExport = () => {
  const { formatMessage } = useIntl()
  const { pathname } = useLocation()
  const projectId = pathname.split('/')[1]
  const {
    authGroupId,
    authUserGroupYear,
    authUserInstituteInternalId,
    authUserId,
    loading: loadingAuth
  } = useAuth()
  const isExportLinkItemDisabled = useReactiveVar(
    isExportLinkItemDisabledVar
  )

  const [isProductionModalOpen, setIsProductionModalOpen] =
    useState<boolean>(false)
  const [uploadType, setUploadType] = useState<UploadTypes>(
    UploadTypes.Final
  )

  const [isStatusModalOpen, setIsStatusModalOpen] =
    useState<boolean>(false)
  const [status, setStatus] = useState<Status>(Status.needApproval)
  const [pendingProductionType, setPendingProductionType] = useState<
    UploadTypes | null
  >(null)
  const [isPendingModalOpen, setIsPendingModalOpen] =
    useState<boolean>(false)
  const [coverFileId, setCoverFileId] = useState<string>('')
  const [isCoverExporting, setIsCoverExporting] =
    useState<boolean>(false)

  const closeProductionModal = () => setIsProductionModalOpen(false)
  const closeStatusModal = () => setIsStatusModalOpen(false)

  const openModal = (uploadType: UploadTypes) => {
    setUploadType(uploadType)
    setIsProductionModalOpen(true)
  }

  const onSubmitSuccess = () => {
    setIsProductionModalOpen(false)
    setPendingProductionType(uploadType)
    setIsPendingModalOpen(true)
    isExportLinkItemDisabledVar(true)
  }

  const closePendingModal = () => {
    setIsPendingModalOpen(false)
  }

  const { data: projectMetaData, loading: loadingProjectMeta } =
    useQuery(GET_PROJECT_META, {
      variables: {
        projectId
      },
      skip: !authGroupId
    })

  const { data: projectTokenData } = useQuery(GET_PROJECT_TOKEN, {
    variables: {
      groupId: authGroupId!,
      projectId
    },
    skip: !authGroupId || !projectId
  })
  const projectToken = projectTokenData?.getProjectToken

  const {
    data: productionAttrs,
    loading: loadingProductionAttrs,
    refetch: refetchProductionAttrs,
    networkStatus: productionAttrsNetworkStatus
  } = useQuery(GET_PRODUCTION_ATTRS, {
    variables: {
      orderNumber: projectMetaData?.getProjectMeta?.order as string,
      year: authUserGroupYear as number,
      customerNumber: authUserInstituteInternalId as string
    },
    skip: !projectMetaData?.getProjectMeta,
    notifyOnNetworkStatusChange: true
  })

  const { data: productionExportsData } = useQuery(
    GET_PRODUCTION_EXPORTS,
    {
      variables: {
        groupId: authGroupId!,
        projectId
      },
      skip: !authGroupId || !projectId || !pendingProductionType,
      pollInterval: pendingProductionType ? 5000 : 0,
      fetchPolicy: 'network-only'
    }
  )

  useEffect(() => {
    if (!pendingProductionType || !productionExportsData?.getProductionExports?.length) {
      return
    }

    const expectedType =
      pendingProductionType === UploadTypes.Final
        ? ProductionExportType.Final
        : ProductionExportType.Proof

    const matchingExports = productionExportsData.getProductionExports.filter(
      (item: { type: string }) => item.type === expectedType
    )
    const latest = matchingExports.sort(
      (a: { startedAt: string }, b: { startedAt: string }) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    )[0]

    if (!latest || latest.status === ProductionExportStatuses.InProgress) {
      return
    }

    setPendingProductionType(null)
    setIsPendingModalOpen(false)

    if (latest.status === ProductionExportStatuses.Completed) {
      setStatus(
        pendingProductionType === UploadTypes.Final
          ? Status.prepareForProduction
          : Status.checkReport
      )
      setIsStatusModalOpen(true)
      isExportLinkItemDisabledVar(false)
      refetchProductionAttrs()
      return
    }

    setStatus(Status.exportFailed)
    setIsStatusModalOpen(true)
    isExportLinkItemDisabledVar(false)
  }, [
    pendingProductionType,
    productionExportsData?.getProductionExports,
    refetchProductionAttrs
  ])

  const loadingListItems =
    loadingProjectMeta ||
    loadingProductionAttrs ||
    loadingAuth ||
    productionAttrsNetworkStatus === NetworkStatus.refetch

  const {
    preparingForProduction,
    proofCopySentToProd,
    hasApprovalDate,
    needsApproval
  } = useProductionAttributes(productionAttrs)

  useEffect(() => {
    if (needsApproval) {
      setStatus(Status.needApproval)
      setIsStatusModalOpen(true)
    }
  }, [needsApproval])

  const [startExport, { loading: isStartExportLoading }] =
    useMutation(START_PROJECT_EXPORT, {
      refetchQueries: [
        {
          query: GET_PRODUCTION_EXPORTS,
          variables: {
            groupId: authGroupId!,
            projectId
          }
        }
      ]
    })

  const [
    startCoverPdfExport,
    { loading: isStartCoverExportLoading }
  ] = useMutation(START_COVER_PDF_EXPORT as DocumentNode)

  const [checkForCoverFile] = useLazyQuery(
    CHECK_FOR_COVER_FILE as DocumentNode,
    {
      fetchPolicy: 'network-only'
    }
  )

  const [checkIfExportInProgress] = useLazyQuery(
    CHECK_IF_EXPORT_IS_IN_PROGRESS,
    {
      variables: { groupId: authGroupId!, projectId },
      fetchPolicy: 'network-only'
    }
  )

  const handleStartPreviewExport = async () => {
    const { data } = await checkIfExportInProgress()

    if (data?.checkIfExportIsInProgress) {
      toast.error('Another export is already in progress')
      return
    }

    await toast.promise(
      startExport({
        variables: {
          groupId: authGroupId!,
          projectId,
          outputProfile: OUTPUT_TYPES[0].apiKey,
          userId: authUserId!
        }
      }),
      {
        loading: formatMessage({
          id: 'Project.export.preview.start.export.loading'
        }),
        success: formatMessage({
          id: 'Project.export.preview.start.export.success'
        }),
        error: formatMessage({
          id: 'Project.export.preview.start.export.error'
        })
      }
    )
  }

  useInterval(async () => {
    if (
      !coverFileId ||
      !isCoverExporting ||
      !authGroupId ||
      !projectToken
    ) {
      return
    }

    const { data } = await checkForCoverFile({
      variables: {
        projectId,
        groupId: authGroupId,
        coverFileId
      }
    })

    if (!data?.checkForCoverFile) {
      return
    }

    setIsCoverExporting(false)
    setCoverFileId('')
    downloadCoverPdf({
      projectId,
      groupId: authGroupId,
      coverFileId,
      token: projectToken
    })
    toast.success(formatMessage({ id: 'Project.export.cover.ready' }))
  }, 3000)

  const handleStartCoverPreviewExport = async () => {
    if (
      !authGroupId ||
      isCoverExporting ||
      isStartCoverExportLoading
    ) {
      return
    }

    const result = await toast.promise(
      startCoverPdfExport({
        variables: {
          projectId
        }
      }),
      {
        loading: formatMessage({
          id: 'Project.export.cover.start.loading'
        }),
        success: formatMessage({
          id: 'Project.export.cover.start.success'
        }),
        error: formatMessage({
          id: 'Project.export.cover.start.error'
        })
      }
    )

    const newCoverFileId = result?.data?.startCoverPdfExport
    if (typeof newCoverFileId !== 'string' || !newCoverFileId) {
      toast.error(
        formatMessage({ id: 'Project.export.cover.start.error' })
      )
      return
    }

    setCoverFileId(newCoverFileId)
    setIsCoverExporting(true)
  }

  return (
    <>
      <IslandLayout
        showTools={false}
        Header={
          <PageHeader
            titleId="Project.export.title"
            titleProps={{
              orderNumber: projectMetaData?.getProjectMeta.order
                ? projectMetaData.getProjectMeta.order
                : null
            }}
            withBackButton
          />
        }
      >
        <Head titleId="Project.export.head.title" />
        <Flex columnGap={108} marginBottom={48}>
          <Section
            titleI18nId="Project.export.preview.title"
            textI18nId="Project.export.preview.text"
          >
            <Flex direction="column" rowGap={16}>
              <ListItem
                id="exportPreviewPdf"
                onClick={handleStartPreviewExport}
                isDisabled={isStartExportLoading}
              />

              <ListItem
                id="exportCoverPreviewPdf"
                onClick={handleStartCoverPreviewExport}
                isDisabled={
                  isCoverExporting || isStartCoverExportLoading
                }
                loading={
                  isCoverExporting || isStartCoverExportLoading
                }
              />
            </Flex>
          </Section>
          <Section
            titleI18nId="Project.export.production.title"
            textI18nId="Project.export.production.text"
          >
            <Flex direction="column" rowGap={16}>
              {hasApprovalDate ? (
                <YouMadeIt
                  deliveryDate={
                    productionAttrs?.getProductionAttributes
                      ?.orderDeliveryDate
                  }
                />
              ) : (
                <>
                  <ListItem
                    id="sampleProduction"
                    onClick={() => openModal(UploadTypes.Proof)}
                    isDisabled={
                      loadingListItems ||
                      proofCopySentToProd ||
                      preparingForProduction ||
                      needsApproval ||
                      isExportLinkItemDisabled
                    }
                    hasTag={
                      proofCopySentToProd ||
                      preparingForProduction ||
                      needsApproval
                    }
                    loading={loadingListItems}
                  />
                  <ListItem
                    id={
                      preparingForProduction
                        ? 'dataUploaded'
                        : 'finalProduction'
                    }
                    onClick={() => openModal(UploadTypes.Final)}
                    hasTag={preparingForProduction || needsApproval}
                    tagId={
                      preparingForProduction
                        ? 'preparingForProduction'
                        : 'needsApproval'
                    }
                    isDisabled={
                      loadingListItems ||
                      preparingForProduction ||
                      isExportLinkItemDisabled
                    }
                    loading={loadingListItems}
                  />
                </>
              )}
            </Flex>
          </Section>
        </Flex>
        <Flex>
          <Section
            titleI18nId="Project.export.history.title"
            textI18nId="Project.export.history.text"
          >
            <History
              refetchProductionAttrs={refetchProductionAttrs}
              projectToken={projectToken || ''}
            />
          </Section>
        </Flex>
      </IslandLayout>
      <ProductionModal
        isModalOpen={isProductionModalOpen}
        closeModal={closeProductionModal}
        orderNumber={projectMetaData?.getProjectMeta?.order}
        uploadType={uploadType}
        modalWidth={MODAL_WIDTH}
        onSubmitSuccess={onSubmitSuccess}
      />
      <ExportPendingModal
        isOpen={!!pendingProductionType && isPendingModalOpen}
        closeModal={closePendingModal}
        modalWidth={MODAL_WIDTH}
      />
      <StatusModal
        closeModal={closeStatusModal}
        isOpen={isStatusModalOpen}
        modalWidth={MODAL_WIDTH}
        status={status}
      />
    </>
  )
}

export default ProjectExport
